import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {GenericSelectModel} from '@app/models/generic-select.model';
import {LookupsDataService} from '@services/lookups-data.service';
import {MessageService} from 'primeng/api';
import {StateLookupModel} from '@app/models/lookups.model';
import {AddressModel, SmartyStreetsModel, SmartyStreetsSuggestionsModel, suggestionsModel, VerifyAddressModel} from '@app/models/addresses.model';
import {AddressesService} from '@services/addresses.service';
import {LookupsService} from '@services/lookups.service';
import {AutoComplete} from 'primeng/autocomplete';
import {SharedImports} from '@shared/shared-imports';
import {ContextService} from '@services/context.service';

@Component({
  selector: 'app-address-form-custom',
  templateUrl: './address-form-custom.component.html',
  styleUrls: ['./address-form-custom.component.scss'],
  imports: [SharedImports]
})

export class AddressFormCustomComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  addressForm: FormGroup;
  inputObjState: GenericSelectModel;
  inputObjCountry: GenericSelectModel;
  setStateId: number;
  setCountryId: number = 1;
  international: boolean;
  suggestAddress: boolean = true;
  suggestAddressJSON: SmartyStreetsModel;
  suggestions: SmartyStreetsSuggestionsModel[];
  autocompleteData: suggestionsModel = {} as suggestionsModel;
  autocompleteItemData: SmartyStreetsSuggestionsModel[] = [];
  verifyAddress: VerifyAddressModel = {} as VerifyAddressModel;
  latitude: number;
  longitude: number;
  currentAddress: string;
  cancelSuggestAddress: boolean;
  stateList: any[];
  @ViewChild('autocomplete') autocompleteElement: AutoComplete;
  @Input() showZipPlus4: boolean;
  @Input() showCounty: boolean;
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() requiredCounty: boolean;
  @Input() canTabWrite: boolean;
  @Input() mapDialogDataTitle: string;
  @Input() mapDialogHeader: string;
  @Input() incomingAddressMailing: AddressModel;
  @Input() incomingAddressBilling: AddressModel;
  @Input() showCategory: boolean = false;
  @Output() outgoingAddress: EventEmitter<AddressModel> = new EventEmitter<AddressModel>();

  private ngUnsubscribe = new Subject();

  constructor(private formBuilder: FormBuilder, private changeDetector: ChangeDetectorRef,
              private lookupsDataService: LookupsDataService, public contextService: ContextService,
              private messageService: MessageService, private addressesService: AddressesService,
              private lookupsService: LookupsService) {
    this.addressForm = this.formBuilder.group({
      category: new FormControl(null, Validators.required),
      country: new FormControl(null),
      streetAddress: new FormControl(null, Validators.maxLength(100)),
      streetAddress2: new FormControl(null, Validators.maxLength(100)),
      city: new FormControl(null, Validators.maxLength(100)),
      state: new FormControl(null),
      postalCode: new FormControl(null, Validators.maxLength(10)),
      zip4: new FormControl(null, Validators.maxLength(4)),
      county: new FormControl(null, Validators.maxLength(100))
    });
  }

  ngOnInit(): void {
    this.suggestAddress = true;
    this.addressForm.markAsPristine();
    this.addressForm.markAsUntouched();
    this.initForm();

    this.addressForm.valueChanges.subscribe({
      next: () => {
        if (this.addressForm.dirty) {
          this.processData();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit(): void {
    this.changeDetector.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.incomingAddressBilling && this.incomingAddressBilling) {
      this.initForm();
    }
    if (this.disabled) {
      this.addressForm.get('streetAddress').disable();
      this.addressForm.get('streetAddress2').disable();
      this.addressForm.get('city').disable();
      this.addressForm.get('state').disable();
      this.addressForm.get('postalCode').disable();
      this.addressForm.get('zip4').disable();
      this.initState(true);

    } else {
      this.addressForm.get('streetAddress').enable();
      this.addressForm.get('streetAddress2').enable();
      this.addressForm.get('city').enable();
      this.addressForm.get('state').enable();
      this.addressForm.get('postalCode').enable();
      this.addressForm.get('zip4').enable();
      this.addressForm.get('streetAddress').setValue(null);
      this.addressForm.get('streetAddress2').setValue(null);
      this.addressForm.get('city').setValue(null);
      this.addressForm.get('state').setValue(null);
      this.addressForm.get('postalCode').setValue(null);
      this.addressForm.get('zip4').setValue(null);
      this.setStateId = null;
      this.initState(false);

    }
    if (changes.incomingAddressMailing && this.incomingAddressMailing) {
      this.initForm();
    }
  }

  initForm(): void {
    this.addressForm.markAsPristine();
    this.addressForm.markAsUntouched();
    this.setCountryId = 1;
    this.addressForm.get('country').setValue(1);
    // this.international = (this.incomingAddressBilling.CountryID !== 1);
    if (this.incomingAddressBilling) {
      this.addressForm.get('streetAddress').setValue(this.incomingAddressBilling.Address1);
      this.addressForm.get('streetAddress2').setValue(this.incomingAddressBilling.Address2);
      this.addressForm.get('city').setValue(this.incomingAddressBilling.City);
      this.setStateId = this.incomingAddressBilling.StateProvinceID;
      this.addressForm.get('state').setValue(this.incomingAddressBilling.StateProvinceID);
      this.addressForm.get('postalCode').setValue(this.incomingAddressBilling.Zip);
      this.addressForm.get('zip4').setValue(this.incomingAddressBilling.ZipPlus4);
    }

    if (this.incomingAddressMailing) {
      this.addressForm.get('streetAddress').setValue(this.incomingAddressMailing.Address1);
      this.addressForm.get('streetAddress2').setValue(this.incomingAddressMailing.Address2);
      this.addressForm.get('city').setValue(this.incomingAddressMailing.City);
      this.setStateId = this.incomingAddressMailing.StateProvinceID;
      this.addressForm.get('state').setValue(this.incomingAddressMailing.StateProvinceID);
      this.addressForm.get('postalCode').setValue(this.incomingAddressMailing.Zip);
      this.addressForm.get('zip4').setValue(this.incomingAddressMailing.ZipPlus4);
    }

    this.initSelects();
  }

  initSelects(): void {
    this.initState(false);
  }

  search(event: any): void {
    if (typeof this.addressForm.get('streetAddress').value !== 'object') {
      this.currentAddress = this.addressForm.get('streetAddress').value;
    }
    this.autocompleteItemData = [];
    if (this.suggestAddress === true && this.cancelSuggestAddress === false && (this.setCountryId === 1 || this.setCountryId === 5)) {
      // this.addressForm.get('streetAddress').valueChanges
      //   .pipe(debounceTime(500))
      //   .subscribe({next:(value) => {
      const reAlpha = new RegExp('[A-Za-z]');
      const reNum = new RegExp('[0-9]');
      // this.autocompleteItemData = [];
      if (reAlpha.test(event.query) && reNum.test(event.query)) {
        this.suggestAddressJSON = {
          country: (this.setCountryId === 5) ? 'CAN' : 'USA',
          prefix: event.query,
          city_filter: this.addressForm.get('city').value,
          state_filter: null
        };
        this.addressesService.getAddressSuggestions(this.suggestAddressJSON)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: (res) => {
              this.suggestions = res.suggestions;
              if (this.suggestions) {
                this.suggestions.forEach(suggestion => {
                  this.autocompleteItemData.push({
                    street_line: suggestion.street_line,
                    city: suggestion.city,
                    state: suggestion.state,
                    zipcode: suggestion.zipcode
                  });
                });
                this.autocompleteData.items = this.autocompleteItemData;
              }
            }
          });
      }
      //}});
    } else {
      this.cancelSuggestAddress = false;
      this.addressForm.get('streetAddress').setValue(this.currentAddress);
    }
  }

  applySuggestion(event: any): void {
    if (this.cancelSuggestAddress === false) {
      if (event?.value?.street_line) {
        this.addressForm.get('streetAddress').setValue(event?.value?.street_line);
        this.addressForm.get('streetAddress2').setValue(null);
      }
      if (event.value?.city) {
        this.addressForm.get('city').setValue(event.value?.city);
      }
      if (event.value?.state) {
        let stSelected: StateLookupModel[];
        this.lookupsService.getStatesUnauthLookup(this.contextService.contextObject.tenantName, this.setCountryId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: (res) => {
              stSelected = res.filter(x => x.Abbreviation === event.value?.state);
              if (stSelected && stSelected[0].ID) {
                this.setStateId = stSelected[0].ID;
                this.initState(false);
                this.addressForm.get('state').setValue(this.setStateId);
              }
            }
          });
      }
      if (event.value?.zipcode) {
        this.addressForm.get('postalCode').setValue(event.value?.zipcode);
        if (this.setCountryId === 1) {
          this.verifyAddress.addressType = 'us-street-components';
          this.verifyAddress.street = event.value?.street_line;
          this.verifyAddress.city = event.value?.city;
          //this.verifyAddress.state = event.value?.state;
          this.verifyAddress.state = this.stateList.find(x => x.Description.includes(event.value?.state)).ID;
          this.verifyAddress.zipcode = event.value?.zipcode;
        }
      }
      this.addressForm.markAsDirty();
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Your address suggestion has been applied. Please review the new address in it\'s entirety for accuracy. Thanks so much!'
      });
      this.autocompleteData.items = [];
      this.autocompleteElement?.hide();
    } else {
      this.cancelSuggestAddress = false;
      this.addressForm.get('streetAddress').setValue(this.currentAddress);
    }
    this.processData();
  }

  initState(disable: boolean): void {
    this.inputObjState = {
      labelText: 'State/Province',
      optionValue: 'ID',
      optionLabel: 'Description',
      filter: true,
      requiredField: true,
      selectFirstValue: false,
      initSelected: this.setStateId,
      disabled: disable,
      canWrite: this.canTabWrite
    };
    if (this.setCountryId && !this.stateList) {
      this.lookupsDataService.getStatesUnauthLookup(this.contextService.contextObject.tenantName, this.setCountryId).then((lookupData: any[]) => {
        this.inputObjState.data = lookupData;
        this.stateList = lookupData;
        this.inputObjState = Object.assign({}, this.inputObjState);
      });
    }
  }

  getStateData(event: any): void {
    if (event && event[0] && event[0].ID) {
      this.setStateId = event[0].ID;
    } else {
      this.setStateId = null;
    }
    this.addressForm.markAsDirty();
    this.addressForm.get('state').setValue(this.setStateId);
  }

  processData(): void {
    const addressToSave: AddressModel = {} as AddressModel;
    if (this.verifyAddress?.street) {
      addressToSave.Address1 = this.verifyAddress.street;
      addressToSave.Address2 = this.verifyAddress.street2;
      addressToSave.City = this.verifyAddress.city;
      addressToSave.StateProvinceID = +this.verifyAddress.state;
      addressToSave.Zip = this.verifyAddress.zipcode;

    } else {
      addressToSave.Address1 = this.addressForm.get('streetAddress').value;
      addressToSave.Address2 = this.addressForm.get('streetAddress2').value;
      addressToSave.City = this.addressForm.get('city').value;
      addressToSave.StateProvinceID = this.setStateId;
      addressToSave.Zip = this.addressForm.get('postalCode').value;
    }
    this.outgoingAddress.emit(addressToSave);
  }

}
